import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from "react"
import { useNavigate } from "react-router-dom"
import { ArrowDown, ArrowUp, ArrowUpDown, Filter } from "lucide-react"
import { CellContext, createColumnHelper } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import ActionMenu from "../ActionMenu"
import {
  TableCellTyped,
  getUserUidDisplayLabel,
  getUserUidDisplayNameAndEmail,
  hydrateUserDisplayCacheFromAuthForUids,
  preloadUsersRelationLabels,
} from "../TableHelper.tsx"
import { isPictureFieldName, resolveConnectorAssetPublicUrl } from "../../utils/pictureUtils"
import { resolveItemCreatorUid, resolveItemOwnerUid, uidsEqual } from "../resolveItemOwnerUid"
import {
  buildAdminMongoDocumentUrl,
  getMongoCollectionNameForFolder,
  mongoCollectionNameForInspector,
} from "../../utils/collectionUtils"
import { isFormSubmissionRow } from "../../utils/formSubmissionRowUtils"
import type { ActionDefinition } from "../../api/actionsApi"
import {
  TABLE_HEADER_CONTROLS_WIDTH_REM,
  TABLE_HEADER_TITLE_MIN_CHARS,
  TABLE_LABELED_COLUMN_MIN_SIZE,
} from "./tableColumnSizing"

function TableHeaderTitle({
  children,
  title,
}: {
  children: ReactNode
  title?: string
}) {
  const resolvedTitle =
    title ?? (typeof children === "string" ? children : undefined)
  return (
    <span
      className="truncate text-xs"
      style={{
        flex: "1 1 0",
        minWidth: `${TABLE_HEADER_TITLE_MIN_CHARS}ch`,
        maxWidth: `calc(100% - ${TABLE_HEADER_CONTROLS_WIDTH_REM}rem)`,
      }}
      title={resolvedTitle}
    >
      {children}
    </span>
  )
}

type ColumnHelper = ReturnType<typeof createColumnHelper<any>>

/** When the table shows at least one form submission, place Form (`_form`) after select (and UID in debug). */
function reorderColumnsWithFormFirst(columns: any[], debugMode: boolean): any[] {
  const formIdx = columns.findIndex((c) => String(c?.id ?? "") === "_form")
  if (formIdx < 0) {
    return columns
  }
  const formCol = columns[formIdx]
  const rest = columns.filter((_, i) => i !== formIdx)
  const selectIdx = rest.findIndex((c) => String(c?.id ?? "") === "select")
  let insertAt = 0
  if (selectIdx >= 0) {
    insertAt = selectIdx + 1
    if (debugMode) {
      const nextId = String(rest[insertAt]?.id ?? "")
      if (nextId === "uid") {
        insertAt += 1
      }
    }
  }
  rest.splice(insertAt, 0, formCol)
  return rest
}

function TableHeaderFilterPopover({
  filterId,
  open,
  onOpenChange,
  trigger,
  children,
}: {
  filterId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: ReactElement
  children: ReactNode
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="z-[10060] w-64 max-h-[40vh] overflow-y-auto border border-gray-200 bg-white p-2 text-gray-900 shadow-md outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
        data-filter-popup={filterId}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

function FormSubmissionMark({
  row,
  formTitleByUid,
}: {
  row: Record<string, unknown>
  formTitleByUid: Record<string, string>
}) {
  const rawUid = row._formUid
  const formUid =
    typeof rawUid === "string"
      ? rawUid.trim()
      : rawUid != null
        ? String(rawUid).trim()
        : ""
  if (!formUid) {
    return null
  }
  const rawTitle = row._formTitle
  const fromRow =
    typeof rawTitle === "string" && rawTitle.trim() !== "" ? rawTitle.trim() : ""
  const fromRegistry = (formTitleByUid[formUid] ?? "").trim()
  const formTitle = fromRow || fromRegistry || formUid

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="block min-w-0 max-w-full cursor-default truncate text-left text-xs font-medium text-violet-900 dark:text-violet-200"
          aria-label={`${formTitle}. Form id: ${formUid}.`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {formTitle}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className="max-w-sm">
        <div className="space-y-1">
          <p className="text-xs font-medium text-popover-foreground">{formTitle}</p>
          <p className="break-all font-mono text-[11px] text-muted-foreground">{formUid}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

export type UseComponentTableColumnsParams = {
  columnHelper: ColumnHelper
  setColumns: Dispatch<SetStateAction<any>>
  currentSlug: string
  meta: any
  currentMeta: any
  actionMode: boolean
  debugMode: boolean
  selectedRows: any[]
  setSelectedRows: Dispatch<SetStateAction<any[]>>
  activeFilterColumnId: string | null
  setActiveFilterColumnId: Dispatch<SetStateAction<string | null>>
  csvFilters: Record<string, string[]>
  createdRange: { from?: string; to?: string }
  setCreatedRange: Dispatch<SetStateAction<{ from?: string; to?: string }>>
  updatedRange: { from?: string; to?: string }
  setUpdatedRange: Dispatch<SetStateAction<{ from?: string; to?: string }>>
  createdRangeRef: MutableRefObject<{ from?: string; to?: string }>
  updatedRangeRef: MutableRefObject<{ from?: string; to?: string }>
  parseTimestamp: (value: any) => Date | null
  renderFieldFilter: (field: any) => React.ReactNode | null
  isFieldFilterApplied: (field: any) => boolean
  renderCsvButton: (fieldName: string) => React.ReactNode | null
  clearCsvForField: (fieldName: string) => void
  handleImageChange: (item: any, field: any, picture: any) => void
  handleFileChange: (item: any, data: { filename: string }) => void
  handleCheckboxClick: (item: any, field: any) => void
  getResolvedCollection: () => string
  openRelationDialog: (args: { targetSlug: string; uid: string }) => void | Promise<void>
  getItemPermissionFlags: (uid: string | undefined) => {
    canRead: boolean
    canUpdate: boolean
    canDelete: boolean
    canDelegate: boolean
  }
  getItemDelegateActionsVisible: (item: any) => boolean
  openDelegateOwnerDialog: (item: any) => void
  requestRemoveDelegatedOwner: (item: any) => void
  rowHasDelegatedOwner: (item: any) => boolean
  canDeleteSelected: boolean
  canDeleteCollection: boolean
  deletePermissionLoading: boolean
  isItemPermissionPending: (action: import("../../acl/buildItemPermissionBulkRequests").ItemPermissionAction) => boolean
  componentActions: ActionDefinition[]
  handleRunUserAction: (action: ActionDefinition, item: any) => void
  handleShowUser: (userId: string) => void
  handleDeleteClick: (item: any) => void
  handleArchiveClick: (item?: any) => void
  isArchiveMenuDisabledForRow: (row: any) => boolean
  handleRestoreClick: (item?: any) => void
  isRestoreMenuDisabledForRow: (row: any) => boolean
  handelShowDetailsClick: (item: any) => void
  handleOnEdit: (item: any) => void
  handleDuplicate: (item: any) => void
  /** Current table rows; used to resolve creator/owner UIDs via /auth/user when collection preload fails. */
  tableRowsForUserHydration?: any[] | undefined
  /** Select-based column filters; listed so header "active filter" styling stays in sync. */
  selectFilters?: Record<string, string[]>
  /** Public form uid → title (from form configs API) for rows missing _formTitle. */
  formTitleByUid?: Record<string, string>
  /** When true, row action menu can include “Show in Mongo” for Mongo-backed components. */
  isAdmin: boolean
  onFormSubmissionShowUser?: (row: Record<string, unknown>) => void
  onFormSubmissionShowForm?: (row: Record<string, unknown>) => void
  onFormSubmissionShowLogs?: () => void
  onFormSubmissionImpersonateUser?: (row: Record<string, unknown>) => void
  impersonatingSubmissionUid?: string | null
  currentUserUid?: string | null
}

/** Read latest filter UI from props ref so header renders stay in sync while typing (columns rebuild rarely). */
function currentFilterHeaderApi(pRef: MutableRefObject<UseComponentTableColumnsParams>) {
  const cur = pRef.current
  return {
    activeFilterColumnId: cur.activeFilterColumnId,
    setActiveFilterColumnId: cur.setActiveFilterColumnId,
    renderFieldFilter: cur.renderFieldFilter,
    isFieldFilterApplied: cur.isFieldFilterApplied,
    renderCsvButton: cur.renderCsvButton,
    clearCsvForField: cur.clearCsvForField,
    csvFilters: cur.csvFilters,
    createdRange: cur.createdRange,
    setCreatedRange: cur.setCreatedRange,
    updatedRange: cur.updatedRange,
    setUpdatedRange: cur.setUpdatedRange,
    createdRangeRef: cur.createdRangeRef,
    updatedRangeRef: cur.updatedRangeRef,
  }
}

export function useComponentTableColumns(p: UseComponentTableColumnsParams): void {
  const navigate = useNavigate()
  const pRef = useRef(p)
  pRef.current = p
  const [userLabelEpoch, setUserLabelEpoch] = useState(0)

  const creatorOwnerUidKey = useMemo(() => {
    const rows = Array.isArray(p.tableRowsForUserHydration) ? p.tableRowsForUserHydration : []
    const uidSet = new Set<string>()
    for (const row of rows) {
      const c = resolveItemCreatorUid(row as Record<string, unknown>)
      const o = resolveItemOwnerUid(row as Record<string, unknown>)
      if (c) uidSet.add(String(c).trim())
      if (o) uidSet.add(String(o).trim())
    }
    return [...uidSet].sort().join("|")
  }, [p.tableRowsForUserHydration])

  useEffect(() => {
    let cancelled = false
    void preloadUsersRelationLabels()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setUserLabelEpoch((n) => n + 1)
      })
    return () => {
      cancelled = true
    }
  }, [p.currentSlug])

  useEffect(() => {
    let cancelled = false
    const uids = creatorOwnerUidKey ? creatorOwnerUidKey.split("|") : []
    void (async () => {
      await hydrateUserDisplayCacheFromAuthForUids(uids)
      if (!cancelled) setUserLabelEpoch((n) => n + 1)
    })()
    return () => {
      cancelled = true
    }
  }, [p.currentSlug, creatorOwnerUidKey])

  const defaultSortingFn = useCallback((rowA: any, rowB: any, columnId: string) => {
    const a = rowA.getValue(columnId)
    const b = rowB.getValue(columnId)

    if (a?.seconds !== undefined && b?.seconds !== undefined) {
      return a.seconds > b.seconds ? 1 : -1
    }
    if (a?.seconds !== undefined) return 1
    if (b?.seconds !== undefined) return -1

    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1

    if (a > b) return 1
    if (a < b) return -1
    return 0
  }, [])

  useEffect(() => {
    const {
      columnHelper,
      setColumns,
      currentSlug,
      meta,
      currentMeta,
      actionMode,
      debugMode,
      selectedRows,
      setSelectedRows,
      activeFilterColumnId,
      setActiveFilterColumnId,
      csvFilters,
      createdRange,
      setCreatedRange,
      updatedRange,
      setUpdatedRange,
      createdRangeRef,
      updatedRangeRef,
      parseTimestamp,
      renderFieldFilter,
      isFieldFilterApplied,
      renderCsvButton,
      clearCsvForField,
      handleImageChange,
      handleFileChange,
      handleCheckboxClick,
      getResolvedCollection,
      openRelationDialog,
      getItemPermissionFlags,
      canDeleteSelected,
      canDeleteCollection,
      deletePermissionLoading,
      isItemPermissionPending,
      componentActions,
      handleRunUserAction,
      handleShowUser,
      handleDeleteClick,
      handleArchiveClick,
      isArchiveMenuDisabledForRow,
      handleRestoreClick,
      isRestoreMenuDisabledForRow,
      handelShowDetailsClick,
      handleOnEdit,
      handleDuplicate,
      getItemDelegateActionsVisible,
      openDelegateOwnerDialog,
      requestRemoveDelegatedOwner,
      rowHasDelegatedOwner,
      formTitleByUid = {},
      isAdmin,
      onFormSubmissionShowUser,
      onFormSubmissionShowForm,
      onFormSubmissionShowLogs,
      onFormSubmissionImpersonateUser,
      impersonatingSubmissionUid,
      currentUserUid,
      tableRowsForUserHydration,
    } = pRef.current

    const _columns: any[] = []
    const mongoMeta = meta || currentMeta
    const mergedMetaForInspector =
      meta && currentMeta ? { ...currentMeta, ...meta } : mongoMeta
    const mongoName = mongoCollectionNameForInspector(mergedMetaForInspector)
    const isMongo = mongoMeta?.dataSource === "mongodb"
    const metaToUse = meta
    if (metaToUse && metaToUse.fields) {
      if (!actionMode) {
        _columns.push(
          columnHelper.accessor("select", {
            id: "select",
            header: ({ table }) => (
              <Checkbox
                checked={
                  table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) => {
                  table.toggleAllPageRowsSelected(!!value)
                  const selected = value
                    ? table.getFilteredRowModel().rows.map((row) => row.original)
                    : []
                  setSelectedRows(selected)
                }}
                aria-label="Select all"
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => {
                  row.toggleSelected(!!value)

                  setSelectedRows((prev) => {
                    if (value) {
                      return [...prev, row.original].filter(
                        (v, i, a) => a.findIndex((t) => t.uid === v.uid) === i
                      )
                    }
                    return prev.filter(
                      (item) => item.uid !== (row.original as { uid: string }).uid
                    )
                  })
                }}
                aria-label="Select row"
              />
            ),
            enableSorting: false,
            enableHiding: false,
          })
        )
      }

      if (debugMode) {
        _columns.push(
          columnHelper.accessor(
            (row: any) => {
              return row.uid ?? row._id ?? row.id ?? ""
            },
            {
              id: "uid",
              header: () => <span className="text-xs">UID</span>,
              cell: (info: CellContext<any, any>) => {
                const value = info.getValue()
                return (
                  <span className="text-xs font-mono text-gray-600 dark:text-gray-300" title={value}>
                    {value || ""}
                  </span>
                )
              },
              enableSorting: false,
              enableHiding: false,
            }
          )
        )
      }

      const connectorAssetPublicUrl = resolveConnectorAssetPublicUrl({
        ...metaToUse,
        slug: metaToUse?.slug || currentSlug,
      })
      const fieldsWithOrder = metaToUse.fields.map((field: any, index: number) => {
        const withOrder =
          field.order === undefined && field.order !== 0 ? { ...field, order: index } : { ...field }
        const needsConnectorAssets =
          Boolean(connectorAssetPublicUrl) &&
          (field?.type === "image" || isPictureFieldName(field?.name))
        return needsConnectorAssets
          ? {
            ...withOrder,
            connectorAssetPublicUrl,
            source: metaToUse?.source,
            collectionSlug: metaToUse?.slug || currentSlug,
          }
          : withOrder
      })
      const sortedFields = [...fieldsWithOrder].sort(
        (a: any, b: any) => (a.order || 0) - (b.order || 0)
      )

      sortedFields.map((field: any, index: number) => {
        if (field?.type === "instruction") {
          return
        }

        const fieldName = field.name || `field_${index}`
        if (!fieldName) {
          console.warn("Skipping column with no name:", field)
          return
        }

        _columns.push(
          columnHelper.accessor(fieldName, {
            id: fieldName,
            size: TABLE_LABELED_COLUMN_MIN_SIZE,
            minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
            sortingFn: defaultSortingFn,
            header: ({ column }) => {
              const {
                activeFilterColumnId,
                setActiveFilterColumnId,
                renderFieldFilter,
                isFieldFilterApplied,
              } = currentFilterHeaderApi(pRef)
              const sortState = column.getIsSorted()
              const isActiveFilter = activeFilterColumnId === fieldName
              const hasAppliedFilter = isFieldFilterApplied(field)
              const label = field.label || field.name || fieldName

              return (
                <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                  <TableHeaderTitle title={label}>{label}</TableHeaderTitle>
                  <TableHeaderFilterPopover
                    filterId={fieldName}
                    open={isActiveFilter}
                    onOpenChange={(open) => {
                      if (open) setActiveFilterColumnId(fieldName)
                      else setActiveFilterColumnId((prev) => (prev === fieldName ? null : prev))
                    }}
                    trigger={
                      <button
                        type="button"
                        data-filter-trigger={fieldName}
                        className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${isActiveFilter || hasAppliedFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                        aria-label={`Filter ${label}`}
                      >
                        <Filter
                          className={`h-3.5 w-3.5 ${isActiveFilter || hasAppliedFilter ? "text-white" : ""}`}
                        />
                      </button>
                    }
                  >
                    {renderFieldFilter(field)}
                  </TableHeaderFilterPopover>
                  <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0"
                    title={`Sort by ${label}`}
                    aria-label={`Sort by ${label}`}
                  >
                    {sortState === "asc" ? (
                      <ArrowUp className="h-3 w-3 shrink-0" />
                    ) : sortState === "desc" ? (
                      <ArrowDown className="h-3 w-3 shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 shrink-0" />
                    )}
                  </Button>
                </div>
              )
            },
            cell: (info: CellContext<any, any>) => (
              <TableCellTyped
                value={info}
                meta={field}
                onImageChange={(file: any) =>
                  handleImageChange(info.cell.row.original, field, file)
                }
                onFileChange={(data) => handleFileChange(info.cell.row.original, field, data)}
                onCheckboxClick={() => handleCheckboxClick(info.cell.row.original, field)}
                collection={
                  getMongoCollectionNameForFolder(meta || currentMeta, currentSlug) ??
                  getResolvedCollection()
                }
                onOpenRelation={openRelationDialog}
              />
            ),
          })
        )
      })
    }

    _columns.push(
      columnHelper.accessor(
        (row: any) => {
          const rawUid = (row as Record<string, unknown>)._formUid
          const uid =
            typeof rawUid === "string"
              ? rawUid.trim()
              : rawUid != null
                ? String(rawUid).trim()
                : ""
          if (!uid) return ""
          const rawTitle = (row as Record<string, unknown>)._formTitle
          const fromRow =
            typeof rawTitle === "string" && rawTitle.trim() !== "" ? rawTitle.trim() : ""
          const fromRegistry = (formTitleByUid[uid] ?? "").trim()
          const title = fromRow || fromRegistry
          return `${title}\u0000${uid}`
        },
        {
          id: "_form",
          size: TABLE_LABELED_COLUMN_MIN_SIZE,
          minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
          header: ({ column }) => {
            const {
              activeFilterColumnId,
              setActiveFilterColumnId,
              renderFieldFilter,
              isFieldFilterApplied,
            } = currentFilterHeaderApi(pRef)
            const sortState = column.getIsSorted()
            const hasFormFilter = isFieldFilterApplied({ name: "_form", type: "systemform" })
            return (
              <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                <TableHeaderTitle>Form</TableHeaderTitle>
                <TableHeaderFilterPopover
                  filterId="_form"
                  open={activeFilterColumnId === "_form"}
                  onOpenChange={(open) => {
                    if (open) setActiveFilterColumnId("_form")
                    else setActiveFilterColumnId((prev) => (prev === "_form" ? null : prev))
                  }}
                  trigger={
                    <button
                      type="button"
                      data-filter-trigger="_form"
                      className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${activeFilterColumnId === "_form" || hasFormFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Filter by form"
                    >
                      <Filter
                        className={`h-3.5 w-3.5 ${activeFilterColumnId === "_form" || hasFormFilter ? "text-white" : ""}`}
                      />
                    </button>
                  }
                >
                  {renderFieldFilter({ name: "_form", type: "systemform" })}
                </TableHeaderFilterPopover>
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0"
                  title="Sort by form"
                  aria-label="Sort by form"
                >
                  {sortState === "asc" ? (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  ) : sortState === "desc" ? (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 shrink-0" />
                  )}
                </Button>
              </div>
            )
          },
          cell: (info: CellContext<any, any>) => (
            <FormSubmissionMark
              row={info.row.original as Record<string, unknown>}
              formTitleByUid={formTitleByUid}
            />
          ),
          sortingFn: (rowA, rowB, columnId) => {
            const a = String(rowA.getValue(columnId) ?? "")
            const b = String(rowB.getValue(columnId) ?? "")
            return a.localeCompare(b)
          },
        }
      )
    )

    _columns.push(
      columnHelper.accessor(
        (row: any) => resolveItemCreatorUid(row as Record<string, unknown>) ?? "",
        {
          id: "_creator",
          size: TABLE_LABELED_COLUMN_MIN_SIZE,
          minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
          header: ({ column }) => {
            const {
              activeFilterColumnId,
              setActiveFilterColumnId,
              renderFieldFilter,
              isFieldFilterApplied,
            } = currentFilterHeaderApi(pRef)
            const sortState = column.getIsSorted()
            const hasCreatorFilter = isFieldFilterApplied({ name: "_creator" })
            return (
              <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                <TableHeaderTitle>Creator</TableHeaderTitle>
                <TableHeaderFilterPopover
                  filterId="_creator"
                  open={activeFilterColumnId === "_creator"}
                  onOpenChange={(open) => {
                    if (open) setActiveFilterColumnId("_creator")
                    else setActiveFilterColumnId((prev) => (prev === "_creator" ? null : prev))
                  }}
                  trigger={
                    <button
                      type="button"
                      data-filter-trigger="_creator"
                      className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${activeFilterColumnId === "_creator" || hasCreatorFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Filter by creator"
                    >
                      <Filter
                        className={`h-3.5 w-3.5 ${activeFilterColumnId === "_creator" || hasCreatorFilter ? "text-white" : ""}`}
                      />
                    </button>
                  }
                >
                  {renderFieldFilter({ name: "_creator", type: "systemuser" })}
                </TableHeaderFilterPopover>
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0"
                  title="Sort by creator"
                  aria-label="Sort by creator"
                >
                  {sortState === "asc" ? (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  ) : sortState === "desc" ? (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 shrink-0" />
                  )}
                </Button>
              </div>
            )
          },
          cell: (info: CellContext<any, any>) => {
            const uid = String(info.getValue() ?? "").trim()
            if (!uid) {
              return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
            }
            const who = getUserUidDisplayNameAndEmail(uid)
            const fallback = getUserUidDisplayLabel(uid) || uid
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleShowUser(uid)
                }}
                className="max-w-full w-full min-w-0 overflow-hidden whitespace-normal text-left text-xs text-sky-700 dark:text-sky-300 underline decoration-sky-600/40 hover:decoration-sky-600 break-words [overflow-wrap:anywhere]"
              >
                {who ? (
                  <>
                    <span className="block font-medium">{who.name}</span>
                    {who.email ? (
                      <span className="mt-0.5 block break-all font-normal text-gray-700 dark:text-gray-300">
                        {who.email}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="block break-all font-normal">{fallback}</span>
                )}
              </button>
            )
          },
          sortingFn: (rowA, rowB, columnId) => {
            const idA = String(rowA.getValue(columnId) ?? "").trim()
            const idB = String(rowB.getValue(columnId) ?? "").trim()
            const labelA = (getUserUidDisplayLabel(idA) || idA).toLowerCase()
            const labelB = (getUserUidDisplayLabel(idB) || idB).toLowerCase()
            const cmp = labelA.localeCompare(labelB)
            if (cmp !== 0) return cmp
            return idA.localeCompare(idB)
          },
        }
      )
    )

    _columns.push(
      columnHelper.accessor(
        (row: any) => resolveItemOwnerUid(row as Record<string, unknown>) ?? "",
        {
          id: "_itemOwner",
          size: TABLE_LABELED_COLUMN_MIN_SIZE,
          minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
          header: ({ column }) => {
            const {
              activeFilterColumnId,
              setActiveFilterColumnId,
              renderFieldFilter,
              isFieldFilterApplied,
            } = currentFilterHeaderApi(pRef)
            const sortState = column.getIsSorted()
            const hasOwnerFilter = isFieldFilterApplied({ name: "_itemOwner" })
            return (
              <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                <TableHeaderTitle>Item owner</TableHeaderTitle>
                <TableHeaderFilterPopover
                  filterId="_itemOwner"
                  open={activeFilterColumnId === "_itemOwner"}
                  onOpenChange={(open) => {
                    if (open) setActiveFilterColumnId("_itemOwner")
                    else setActiveFilterColumnId((prev) => (prev === "_itemOwner" ? null : prev))
                  }}
                  trigger={
                    <button
                      type="button"
                      data-filter-trigger="_itemOwner"
                      className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${activeFilterColumnId === "_itemOwner" || hasOwnerFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Filter by item owner"
                    >
                      <Filter
                        className={`h-3.5 w-3.5 ${activeFilterColumnId === "_itemOwner" || hasOwnerFilter ? "text-white" : ""}`}
                      />
                    </button>
                  }
                >
                  {renderFieldFilter({ name: "_itemOwner", type: "systemuser" })}
                </TableHeaderFilterPopover>
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0"
                  title="Sort by item owner"
                  aria-label="Sort by item owner"
                >
                  {sortState === "asc" ? (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  ) : sortState === "desc" ? (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 shrink-0" />
                  )}
                </Button>
              </div>
            )
          },
          cell: (info: CellContext<any, any>) => {
            const uid = String(info.getValue() ?? "").trim()
            if (!uid) {
              return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
            }
            const who = getUserUidDisplayNameAndEmail(uid)
            const fallback = getUserUidDisplayLabel(uid) || uid
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleShowUser(uid)
                }}
                className="max-w-full w-full min-w-0 overflow-hidden whitespace-normal text-left text-xs text-indigo-700 dark:text-indigo-300 underline decoration-indigo-600/40 hover:decoration-indigo-600 break-words [overflow-wrap:anywhere]"
              >
                {who ? (
                  <>
                    <span className="block font-medium">{who.name}</span>
                    {who.email ? (
                      <span className="mt-0.5 block break-all font-normal text-gray-700 dark:text-gray-300">
                        {who.email}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="block break-all font-normal">{fallback}</span>
                )}
              </button>
            )
          },
          sortingFn: (rowA, rowB, columnId) => {
            const idA = String(rowA.getValue(columnId) ?? "").trim()
            const idB = String(rowB.getValue(columnId) ?? "").trim()
            const labelA = (getUserUidDisplayLabel(idA) || idA).toLowerCase()
            const labelB = (getUserUidDisplayLabel(idB) || idB).toLowerCase()
            const cmp = labelA.localeCompare(labelB)
            if (cmp !== 0) return cmp
            return idA.localeCompare(idB)
          },
        }
      )
    )

    _columns.push(
      columnHelper.accessor(
        (row: any) => {
          return row.createdAt ?? row._created
        },
        {
          sortingFn: defaultSortingFn,
          id: "_created",
          size: TABLE_LABELED_COLUMN_MIN_SIZE,
          minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
          header: ({ column }) => {
            const {
              activeFilterColumnId,
              setActiveFilterColumnId,
              renderCsvButton,
              clearCsvForField,
              createdRange,
              setCreatedRange,
              createdRangeRef,
              csvFilters,
            } = currentFilterHeaderApi(pRef)
            const sortState = column.getIsSorted()
            const hasCreatedFilter = Boolean(
              createdRangeRef.current.from ||
              createdRangeRef.current.to ||
              (Array.isArray(csvFilters["_created"]) && csvFilters["_created"].length > 0)
            )
            return (
              <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                <TableHeaderTitle>Created</TableHeaderTitle>
                <TableHeaderFilterPopover
                  filterId="_created"
                  open={activeFilterColumnId === "_created"}
                  onOpenChange={(open) => {
                    if (open) setActiveFilterColumnId("_created")
                    else setActiveFilterColumnId((prev) => (prev === "_created" ? null : prev))
                  }}
                  trigger={
                    <button
                      type="button"
                      data-filter-trigger="_created"
                      className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${activeFilterColumnId === "_created" || hasCreatedFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Filter by created date"
                    >
                      <Filter
                        className={`h-3.5 w-3.5 ${activeFilterColumnId === "_created" || hasCreatedFilter ? "text-white" : ""}`}
                      />
                    </button>
                  }
                >
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      defaultValue={createdRange.from ?? ""}
                      onChange={(e) =>
                        setCreatedRange((prev) => ({
                          ...prev,
                          from: e.target.value || undefined,
                        }))
                      }
                      className="h-8 text-[11px] bg-white dark:bg-white text-[11px]"
                    />
                    <Input
                      type="date"
                      defaultValue={createdRange.to ?? ""}
                      onChange={(e) =>
                        setCreatedRange((prev) => ({
                          ...prev,
                          to: e.target.value || undefined,
                        }))
                      }
                      className="h-8 text-[11px] bg-white dark:bg-white text-[11px]"
                    />
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {renderCsvButton("_created")}
                    <button
                      type="button"
                      className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      onClick={() => {
                        setCreatedRange({})
                        clearCsvForField("_created")
                        setActiveFilterColumnId(null)
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </TableHeaderFilterPopover>
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0 text-xs"
                  title="Sort by created date"
                  aria-label="Sort by created date"
                >
                  {sortState === "asc" ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : sortState === "desc" ? (
                    <ArrowDown className="h-3 w-3" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3" />
                  )}
                </Button>
              </div>
            )
          },
          cell: (info: CellContext<any, any>) => {
            const createdValue =
              info.getValue() ?? info.row.original.createdAt ?? info.row.original._created
            const createdDate = parseTimestamp(createdValue)
            if (createdDate === null) {
              return <div></div>
            }
            return (
              <div
                className="!text-gray-600 dark:!text-gray-400 text-xs break-words"
                style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
              >
                {createdDate.toDateString()} - {createdDate.toLocaleTimeString("en-GB")}
              </div>
            )
          },
        }
      )
    )

    _columns.push(
      columnHelper.accessor(
        (row: any) => {
          return row.updatedAt ?? row._updated
        },
        {
          sortingFn: defaultSortingFn,
          id: "_updated",
          size: TABLE_LABELED_COLUMN_MIN_SIZE,
          minSize: TABLE_LABELED_COLUMN_MIN_SIZE,
          header: ({ column }) => {
            const {
              activeFilterColumnId,
              setActiveFilterColumnId,
              renderCsvButton,
              clearCsvForField,
              updatedRange,
              setUpdatedRange,
              updatedRangeRef,
              csvFilters,
            } = currentFilterHeaderApi(pRef)
            const sortState = column.getIsSorted()
            const hasUpdatedFilter = Boolean(
              updatedRangeRef.current.from ||
              updatedRangeRef.current.to ||
              (Array.isArray(csvFilters["_updated"]) && csvFilters["_updated"].length > 0)
            )
            return (
              <div className="flex w-full items-center gap-1 overflow-hidden px-1 py-0.5">
                <TableHeaderTitle>Updated</TableHeaderTitle>
                <TableHeaderFilterPopover
                  filterId="_updated"
                  open={activeFilterColumnId === "_updated"}
                  onOpenChange={(open) => {
                    if (open) setActiveFilterColumnId("_updated")
                    else setActiveFilterColumnId((prev) => (prev === "_updated" ? null : prev))
                  }}
                  trigger={
                    <button
                      type="button"
                      data-filter-trigger="_updated"
                      className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-transparent ${activeFilterColumnId === "_updated" || hasUpdatedFilter ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white dark:hover:bg-blue-500 dark:hover:text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"}`}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      aria-label="Filter by updated date"
                    >
                      <Filter
                        className={`h-3.5 w-3.5 ${activeFilterColumnId === "_updated" || hasUpdatedFilter ? "text-white" : ""}`}
                      />
                    </button>
                  }
                >
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      defaultValue={updatedRange.from ?? ""}
                      onChange={(e) =>
                        setUpdatedRange((prev) => ({
                          ...prev,
                          from: e.target.value || undefined,
                        }))
                      }
                      className="h-8 text-[11px] bg-white dark:bg-white text-[11px]"
                    />
                    <Input
                      type="date"
                      defaultValue={updatedRange.to ?? ""}
                      onChange={(e) =>
                        setUpdatedRange((prev) => ({
                          ...prev,
                          to: e.target.value || undefined,
                        }))
                      }
                      className="h-8 text-[11px] bg-white dark:bg-white text-[11px]"
                    />
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {renderCsvButton("_updated")}
                    <button
                      type="button"
                      className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      onClick={() => {
                        setUpdatedRange({})
                        clearCsvForField("_updated")
                        setActiveFilterColumnId(null)
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </TableHeaderFilterPopover>
                <Button
                  variant="ghost"
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center p-0 text-xs"
                  title="Sort by updated date"
                  aria-label="Sort by updated date"
                >
                  {sortState === "asc" ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : sortState === "desc" ? (
                    <ArrowDown className="h-3 w-3" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3" />
                  )}
                </Button>
              </div>
            )
          },
          cell: (info: CellContext<any, any>) => {
            const updatedValue =
              info.getValue() ?? info.row.original.updatedAt ?? info.row.original._updated
            const updatedDate = parseTimestamp(updatedValue)
            if (updatedDate === null) {
              return <div></div>
            }
            return (
              <div
                className="!text-gray-600 dark:!text-gray-400 text-xs break-words"
                style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
              >
                {updatedDate.toDateString()} - {updatedDate.toLocaleTimeString("en-GB")}
              </div>
            )
          },
        }
      )
    )

    if (!actionMode) {
      _columns.push(
        columnHelper.display({
          id: "actions",
          cell: (props) => {
            const rowOriginal = props.row.original as { uid?: string }
            const rowPermissions = getItemPermissionFlags(rowOriginal?.uid)
            const canDeleteRow = selectedRows.length > 1 ? canDeleteSelected : rowPermissions.canDelete
            const delegateVisible = getItemDelegateActionsVisible(rowOriginal)
            const rowUid = rowOriginal?.uid != null ? String(rowOriginal.uid).trim() : ""
            const showInMongo = Boolean(isAdmin && isMongo && mongoName && rowUid)
            const rowRecord = rowOriginal as Record<string, unknown>
            const submitterUid = resolveItemCreatorUid(rowRecord)
            const formSubmissionImpersonateDisabled =
              !submitterUid || uidsEqual(submitterUid, currentUserUid)
            const formSubmissionImpersonateLoading = Boolean(
              submitterUid && impersonatingSubmissionUid === submitterUid
            )
            const showFormSubmissionActions = Boolean(
              selectedRows.length <= 1 &&
              isFormSubmissionRow(rowRecord) &&
              ((onFormSubmissionShowUser &&
                onFormSubmissionShowForm &&
                onFormSubmissionShowLogs) ||
                onFormSubmissionImpersonateUser)
            )
            return (
              <div className="flex justify-end">
                <ActionMenu
                  item={rowOriginal}
                  onDelete={() => handleDeleteClick(rowOriginal)}
                  onDetails={() => handelShowDetailsClick(rowOriginal)}
                  onEdit={() => handleOnEdit(rowOriginal)}
                  onDuplicate={() => handleDuplicate(rowOriginal)}
                  onArchive={handleArchiveClick}
                  archiveDisabled={isArchiveMenuDisabledForRow(rowOriginal)}
                  actions={componentActions}
                  onRunAction={handleRunUserAction}
                  selectedRowsCount={selectedRows.length}
                  permissions={{ ...rowPermissions, canDelete: canDeleteRow }}
                  isItemPermissionPending={isItemPermissionPending}
                  onDelegateOwner={() => openDelegateOwnerDialog(rowOriginal)}
                  onRemoveDelegatedOwner={() => requestRemoveDelegatedOwner(rowOriginal)}
                  showRemoveDelegatedOwner={rowHasDelegatedOwner(rowOriginal)}
                  delegateActionsVisible={delegateVisible}
                  showInMongo={showInMongo}
                  onShowInMongo={() => {
                    if (!mongoName || !rowUid) {
                      return
                    }
                    navigate(buildAdminMongoDocumentUrl(mongoName, rowUid))
                  }}
                  showFormSubmissionActions={showFormSubmissionActions}
                  onFormSubmissionShowUser={
                    onFormSubmissionShowUser
                      ? () => onFormSubmissionShowUser(rowRecord)
                      : undefined
                  }
                  onFormSubmissionShowForm={
                    onFormSubmissionShowForm
                      ? () => onFormSubmissionShowForm(rowRecord)
                      : undefined
                  }
                  onFormSubmissionShowLogs={onFormSubmissionShowLogs}
                  onFormSubmissionImpersonateUser={
                    onFormSubmissionImpersonateUser
                      ? () => onFormSubmissionImpersonateUser(rowRecord)
                      : undefined
                  }
                  formSubmissionImpersonateDisabled={formSubmissionImpersonateDisabled}
                  formSubmissionImpersonateLoading={formSubmissionImpersonateLoading}
                />
              </div>
            )
          },
        })
      )
    }

    const tableRowsForForm = Array.isArray(tableRowsForUserHydration) ? tableRowsForUserHydration : []
    const anyFormSubmissionOnPage = tableRowsForForm.some((row) => isFormSubmissionRow(row))
    const hasFormColumn = _columns.some((c) => String(c?.id ?? "") === "_form")
    const columnsOut =
      anyFormSubmissionOnPage && hasFormColumn
        ? reorderColumnsWithFormFirst(_columns, debugMode)
        : _columns
    setColumns(columnsOut)
  }, [
    p.columnHelper,
    defaultSortingFn,
    p.meta,
    p.currentMeta,
    p.currentSlug,
    p.actionMode,
    p.debugMode,
    p.selectedRows,
    p.csvFilters,
    p.createdRange,
    p.updatedRange,
    p.isItemPermissionPending,
    p.canDeleteSelected,
    p.canDeleteCollection,
    p.handleArchiveClick,
    p.handleDuplicate,
    p.isArchiveMenuDisabledForRow,
    p.handleRestoreClick,
    p.isRestoreMenuDisabledForRow,
    p.deletePermissionLoading,
    p.componentActions,
    p.getItemPermissionFlags,
    p.handleRunUserAction,
    p.handleShowUser,
    p.getItemDelegateActionsVisible,
    p.openDelegateOwnerDialog,
    p.requestRemoveDelegatedOwner,
    p.rowHasDelegatedOwner,
    p.openRelationDialog,
    p.getResolvedCollection,
    p.createdRangeRef,
    p.updatedRangeRef,
    p.setSelectedRows,
    p.setCreatedRange,
    p.setUpdatedRange,
    p.setColumns,
    userLabelEpoch,
    p.formTitleByUid,
    p.isAdmin,
    p.onFormSubmissionShowUser,
    p.onFormSubmissionShowForm,
    p.onFormSubmissionShowLogs,
    p.onFormSubmissionImpersonateUser,
    p.impersonatingSubmissionUid,
    p.currentUserUid,
    p.tableRowsForUserHydration,
    navigate,
  ])
}
